import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, GraduationCap, HeartHandshake, Users, Quote } from "lucide-react";
import HeroAnimation from "../components/HeroAnimation";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { useLang } from "../contexts/LanguageContext";
import { t, ACTIVITY_PHOTOS } from "../i18n/translations";
import useSEO from "../hooks/useSEO";

const valueIcons = [ShieldCheck, GraduationCap, HeartHandshake, Users];

// Photos for Get Involved cards (editorial, real human imagery)
const INVOLVE_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/egd9m9p6_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/hu71aprt_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/m8n8mx14_image.png",
];

const Home = () => {
  const { lang } = useLang();
  useSEO({
    title: lang === "en"
      ? "Revelo — Youth Against Scams in Hong Kong"
      : "Revelo — 香港青年反詐騙行動",
    description: lang === "en"
      ? "Revelo is a Hong Kong youth-led initiative empowering communities to recognise, resist and report scams through workshops, partnerships and educational resources."
      : "Revelo 是香港青年發起的反詐騙教育行動，透過工作坊、合作夥伴及教育資源，協助大眾識別、抵禦及舉報詐騙。",
    lang,
  });
  const tr = t[lang];

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed photo */}
      <HeroAnimation />

      {/* WHO WE ARE — editorial intro with image + text */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-6 order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "Who We Are" : "我們是誰"}</p>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{tr.about.title}</h2>
              <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
              <p className="mt-6 text-slate-700 text-lg leading-relaxed">{tr.about.body}</p>
              <Link to="/about" data-testid="about-readmore" className="inline-flex items-center gap-2 mt-7 text-[#163B71] font-bold hover:gap-3 transition-all border-b-2 border-[#163B71] pb-1">
                {lang === "en" ? "Read more about Revelo" : "了解更多 Revelo"} <ArrowRight size={16} className="btn-arrow" />
              </Link>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/7wdepviy_PHOTO-2026-06-23-23-34-59.jpg"
                  alt="Revelo volunteer with elderly participants"
                  className="w-full h-[420px] lg:h-[520px] object-cover rounded-sm"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS — pull quote + cards */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "The Problem" : "問題所在"}</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{tr.why.title}</h2>
            <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
            {tr.why.intro && <p className="mt-6 text-slate-700 text-lg leading-relaxed">{tr.why.intro}</p>}
          </Reveal>
          <StaggerGroup className="mt-14 grid md:grid-cols-3 gap-x-8 gap-y-10">
            {tr.why.cards.map((c, i) => (
              <StaggerItem key={i}>
                <div data-testid={`why-card-${i}`} className="h-full border-t-4 border-[#163B71] pt-6">
                  <p className="font-mono text-xs text-[#E9A229] font-bold tracking-widest">0{i + 1}</p>
                  <h3 className="mt-3 font-heading font-bold text-xl text-[#0A192F] leading-snug">{c.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20 lg:py-28 bg-[#163B71] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <Quote size={48} className="mx-auto text-[#F2B53A]" strokeWidth={1.5} />
            <blockquote className="mt-8 font-heading text-2xl sm:text-3xl lg:text-4xl leading-[1.4] font-medium whitespace-pre-line">
              {lang === "en"
                ? "Digital safety is not just a skill.\nIt is a form of care."
                : "數碼安全不只是一項技能，\n也是一種關懷。"}
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-[0.25em] text-[#F2B53A] font-semibold">
              {lang === "en" ? "— Revelo Team" : "— Revelo 團隊"}
            </p>
          </Reveal>
        </div>
      </section>

      {/* GET INVOLVED — photo-led cards (Girl Up style) */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "Join Our Movement" : "加入我們"}</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{tr.getInvolved.title}</h2>
            <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
            <p className="mt-6 text-slate-700 text-lg leading-relaxed">{tr.getInvolved.intro}</p>
          </Reveal>
          <StaggerGroup className="mt-14 grid md:grid-cols-3 gap-x-8 gap-y-10">
            {tr.getInvolved.cards.map((c, i) => {
              const isVolunteer = c.interest === "Joining as a volunteer" || c.interest === "加入義工團隊";
              const linkProps = isVolunteer
                ? { href: tr.volunteer.formUrl, target: "_blank", rel: "noreferrer" }
                : { to: `/contact?interest=${encodeURIComponent(c.interest)}` };
              const Tag = isVolunteer ? "a" : Link;
              return (
                <StaggerItem key={i}>
                  <Tag
                    {...linkProps}
                    data-testid={`getinvolved-card-${i}`}
                    className="group block h-full img-parallax-wrap"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={INVOLVE_PHOTOS[i]}
                        alt={c.title}
                        className="img-parallax w-full h-72 object-cover"
                      />
                    </div>
                    <h3 className="mt-6 font-heading font-bold text-2xl text-[#0A192F] group-hover:text-[#163B71] transition-colors">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-slate-600 leading-relaxed">{c.body}</p>
                    <span
                      data-testid={`getinvolved-cta-${i}`}
                      className="mt-5 inline-flex items-center gap-2 text-[#163B71] font-bold border-b-2 border-[#E9A229] pb-0.5 group-hover:gap-3 transition-all"
                    >
                      {c.button} <ArrowRight size={16} className="btn-arrow" />
                    </span>
                  </Tag>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
};

export default Home;
